// src/admin/screens/loans/actions/RecordPaymentModal.tsx
// Manual repayment entry — use for cash/bank/out-of-band receipts that the
// aggregator didn't report. For aggregator webhook payments, the webhook
// handler (sub-project A) already inserts the row.
//
// Schema alignment:
//   - direction: 'repayment' (our enum), NOT 'inbound'
//   - provider: free-string; we accept mobile-money networks + 'cash' + 'bank'
//   - provider_ref: optional (for cash we typically have none)
//   - completed_at: when funds were received (also sets initiated_at = same)
//   - user_id: the *borrower*'s auth uid (loan.user_id), not the admin
//   - idempotency_key: per-attempt uuid to guard against double submit
//
// A trigger in sub-project A (loan_apply_payment_trg) allocates the payment
// against loan_repayment_schedule rows and flips loan.status to 'repaid'
// when the last cent is covered. We don't touch the schedule here.
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import { kwachaStringToNgwee } from '@admin/utils/money';
import type { LoanDetail } from '@admin/hooks/useLoan';

const PROVIDERS = [
  'mtn_momo',
  'airtel_money',
  'zamtel_kwacha',
  'cash',
  'bank',
] as const;

const schema = z.object({
  amount: z.string().min(1, 'Amount required'),
  provider: z.enum(PROVIDERS),
  provider_ref: z.string().optional(),
  received_at: z.string().min(10, 'Date required'),
});
type Values = z.infer<typeof schema>;

interface Props {
  loan: LoanDetail;
  onClose: (refresh: boolean) => void;
}

export default function RecordPaymentModal({ loan, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      provider: 'mtn_momo',
      received_at: new Date().toISOString().slice(0, 16),
      amount: '',
      provider_ref: '',
    },
  });

  const submit = async (v: Values) => {
    const ngwee = kwachaStringToNgwee(v.amount);
    if (ngwee == null || ngwee <= 0) {
      toast({ kind: 'error', msg: 'Invalid amount.' });
      return;
    }
    setBusy(true);
    try {
      const whenIso = new Date(v.received_at).toISOString();
      const { error } = await supabase.from('payments').insert({
        loan_id: loan.id,
        user_id: loan.user_id,
        direction: 'repayment',
        status: 'completed',
        amount_ngwee: ngwee,
        provider: v.provider,
        provider_ref: v.provider_ref?.trim() ? v.provider_ref.trim() : null,
        idempotency_key: `manual-repay-${loan.id}-${Date.now()}`,
        initiated_at: whenIso,
        completed_at: whenIso,
      });
      if (error) throw error;
      await recordAudit({
        action: 'loan.record_payment',
        target_type: 'loan',
        target_id: loan.id,
        payload: { amount_ngwee: ngwee, provider: v.provider },
      });
      toast({ kind: 'success', msg: 'Payment recorded' });
      await qc.invalidateQueries({ queryKey: ['loan', loan.id] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose(false)}
      title={`Record payment — ${loan.loan_number}`}
    >
      <form
        onSubmit={handleSubmit(submit)}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <FormField
          label="Amount (K)"
          htmlFor="amt"
          error={errors.amount?.message}
          required
        >
          <input id="amt" inputMode="decimal" {...register('amount')} />
        </FormField>
        <FormField label="Provider" htmlFor="provider" required>
          <select id="provider" {...register('provider')}>
            <option value="mtn_momo">MTN MoMo</option>
            <option value="airtel_money">Airtel Money</option>
            <option value="zamtel_kwacha">Zamtel Kwacha</option>
            <option value="cash">Cash</option>
            <option value="bank">Bank transfer</option>
          </select>
        </FormField>
        <FormField
          label="Provider reference"
          htmlFor="pref"
          hint="Receipt / transaction id (leave blank for cash)"
        >
          <input id="pref" {...register('provider_ref')} />
        </FormField>
        <FormField
          label="Received at"
          htmlFor="rat"
          error={errors.received_at?.message}
          required
        >
          <input id="rat" type="datetime-local" {...register('received_at')} />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Record payment
          </Button>
        </div>
      </form>
    </Modal>
  );
}
